import { useEffect, useRef } from 'react'

/**
 * Custom hook to automatically enter fullscreen mode on Chrome browsers
 * when the component mounts
 * 
 * @returns {Object} Object containing:
 *   - isChrome: boolean indicating if the browser is Chrome
 *   - toggleFullscreen: function to manually toggle fullscreen
 */
export function useAutoFullscreenChrome() {
  const hasAutoFullscreened = useRef(false)

  // Detect if browser is Chrome
  const isChrome = () => {
    const userAgent = navigator.userAgent.toLowerCase()
    const isChromium = userAgent.indexOf('chrome') > -1
    const isEdge = userAgent.indexOf('edg') > -1
    const isBrave = (navigator as any).brave !== undefined
    
    // Return true only for Chrome, not Edge or Brave
    return isChromium && !isEdge && !isBrave
  }

  // Toggle fullscreen function
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(err => {
        console.error('Error attempting to enable fullscreen:', err)
      })
    } else {
      document.exitFullscreen().catch(err => {
        console.error('Error attempting to exit fullscreen:', err)
      })
    }
  }

  // Auto-enter fullscreen on Chrome on mount
  useEffect(() => {
    // Only run once per component mount
    if (hasAutoFullscreened.current) {
      return
    }

    // Only auto-fullscreen on Chrome browsers
    if (isChrome() && !document.fullscreenElement) {
      // Small delay to ensure page is fully loaded
      const timer = setTimeout(() => {
        document.documentElement.requestFullscreen().catch(err => {
          console.error('Error attempting auto-fullscreen on Chrome:', err)
        })
        hasAutoFullscreened.current = true
      }, 500)

      return () => clearTimeout(timer)
    }
  }, [])

  return {
    isChrome: isChrome(),
    toggleFullscreen
  }
}
